/**
 * dedupeUploads — partition an incoming batch of files against the
 * library's live document set and the current in-flight upload slots.
 *
 * Pure, DOM- and network-free so the shell handler stays thin and the
 * duplicate logic is unit-testable in isolation.
 *
 * Three-way split (case-insensitive on the file name):
 *   - blocked — name already exists as a document in the library (see
 *     ``sameName``). These are surfaced to the writer in a popup and NOT
 *     uploaded unless the writer picks Replace.
 *   - skipped — name duplicates an earlier file in the SAME batch (a
 *     flattened folder can carry the same name in two subfolders) or a
 *     file that is still uploading from an earlier batch. Silently
 *     dropped: expected folder clutter, not an error worth a popup.
 *   - allowed — everything else; uploaded as normal.
 *
 * Precedence per file: library collision (blocked) wins over an
 * in-batch / in-flight duplicate (skipped).
 */

export interface PartitionUploadsOptions {
  /** Names of documents that already live in the library (live fetch). */
  libraryNames: string[];
  /** Names of files still uploading from earlier batches (in-flight slots). */
  pendingNames: string[];
}

export interface PartitionUploadsResult {
  allowed: File[];
  blocked: File[];
  skipped: File[];
}

function normalize(name: string): string {
  return name.trim().toLowerCase();
}

/** Lowercased, every run of non-alphanumerics one space — ``library_catalog._norm_name``. */
function normName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * Whether a library document's display name is a file's name.
 *
 * Sangnan drops the extension from the name it shows and turns ``__`` into
 * ``--``, so ``Protocol__v2.docx`` is listed as ``Protocol--v2``. The file's
 * extension is stripped and both are normalised before comparing. Only the
 * file's side is stripped: a display name has no extension left to lose, and
 * stripping it too would read ``Protocol v2.1`` as ``Protocol v2``.
 */
export function sameName(displayName: string, fileName: string): boolean {
  const shown = normName(displayName);
  return shown === normName(fileName.replace(/\.[^./\\]*$/, "")) || shown === normName(fileName);
}

export function partitionUploads(files: File[], opts: PartitionUploadsOptions): PartitionUploadsResult {
  const libraryNames = opts.libraryNames.filter((name) => name.trim() !== "");
  const pendingNames = new Set(opts.pendingNames.map(normalize));
  const seenInBatch = new Set<string>();

  const allowed: File[] = [];
  const blocked: File[] = [];
  const skipped: File[] = [];

  for (const file of files) {
    const key = normalize(file.name);
    if (libraryNames.some((name) => sameName(name, file.name))) {
      blocked.push(file);
      continue;
    }
    if (seenInBatch.has(key) || pendingNames.has(key)) {
      skipped.push(file);
      continue;
    }
    seenInBatch.add(key);
    allowed.push(file);
  }

  return { allowed, blocked, skipped };
}
